import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, TripProduct } from "@b2bportal/core-types";
import { mapQueryResponse } from "@b2bportal/core-utilities";
import { Product } from "@b2bportal/purchase-api";
import { useExperiment } from "@hopper-b2b/experiments";
import { CarReservationCard } from "../components/CarReservationCard/CarReservationCard";
import { commerceTripsApi } from "../store/api";
import { useSessionContext } from "@/components/AuthModule/SessionContext";
import type { GroundBookingContext } from "@/utils/commerce/types";
import { buildCarTripDetailsUrl } from "@/utils/commerce/urls";

export type CarTripProduct = TripProduct<GroundBookingContext>;

/**
 * Hook to fetch and manage car/ground reservations from Commerce API
 */
export const useCarsTripProduct = (): {
  cars: { [Product.Ground]: CarTripProduct };
  pastCars: { [Product.Ground]: CarTripProduct };
} => {
  const dispatch = useDispatch<AppDispatch>();
  const { sessionInfo } = useSessionContext();
  const enableCarsSearch = useExperiment("EnableCarsSearch");
  const hasFetchedRef = useRef(false);

  // Dispatch when experiment flag loads and becomes true
  // The fetch inside useMemo is called only once on component mount, but the experiment flag may not be ready at that time. This ensures we fetch as soon as the flag is enabled.
  React.useEffect(() => {
    if (enableCarsSearch && !hasFetchedRef.current) {
      dispatch(commerceTripsApi.endpoints.fetchAllCarReservations.initiate());
      hasFetchedRef.current = true;
    }
  }, [enableCarsSearch, dispatch]);

  const product = React.useMemo(
    () => ({
      fetch: () => {
        if (enableCarsSearch) {
          dispatch(
            commerceTripsApi.endpoints.fetchAllCarReservations.initiate()
          );
        }
      },
      component: ({ reservation, ...props }) => (
        <CarReservationCard
          onClick={() => {
            window.location.href = buildCarTripDetailsUrl({
              reservationId: reservation.tripId,
              perkspotUserId: sessionInfo?.userDetails.tenantUserId,
              communityId: sessionInfo?.userDetails.communityId,
            });
          }}
          reservation={reservation}
          {...props}
        />
      ),
    }),
    [dispatch, enableCarsSearch, sessionInfo]
  );

  const reservations = useSelector(
    commerceTripsApi.endpoints.fetchAllCarReservations.select()
  );

  return React.useMemo(
    () => ({
      cars: {
        [Product.Ground]: {
          ...product,
          reservations: mapQueryResponse(
            reservations,
            (data) => data.itineraries.upcoming
          ),
        },
      },
      pastCars: {
        [Product.Ground]: {
          ...product,
          reservations: mapQueryResponse(
            reservations,
            (data) => data.itineraries.past
          ),
        },
      },
    }),
    [product, reservations]
  );
};
